import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { pl, enUS } from 'date-fns/locale';
import { Box } from '@chakra-ui/react';
const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const DATE_FNS_LOCALES: Record<string, Locale> = { pl, en: enUS };

// i18n.language może mieć wariant regionalny (np. 'en-US'), a nie 'en'
const getLocale = (language: string): Locale =>
  DATE_FNS_LOCALES[language.split('-')[0]] ?? pl;

const getMonthNames = (locale: Locale): string[] =>
  Array.from({ length: 12 }, (_, month) =>
    capitalize(locale.localize?.month(month, { width: 'wide' })),
  );

// kolejność zawsze od niedzieli, tak jak oczekuje datepicker
const getDayNames = (locale: Locale): string[] =>
  Array.from({ length: 7 }, (_, day) =>
    capitalize(locale.localize?.day(day, { width: 'short' }).slice(0, 2)),
  );

interface InputDateRangeProps {
  value: Date[] | undefined;
  min: Date | undefined;
  max: Date | undefined;
  isDisabled?: boolean;
  onChange: (value: Date[]) => void;
  [key: string]: any;
}

export const InputDateRange = ({
  value,
  min,
  max,
  isDisabled,
  onChange,
  ...props
}: InputDateRangeProps): JSX.Element => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const locale = React.useMemo(() => getLocale(language), [language]);
  const monthNames = React.useMemo(() => getMonthNames(locale), [locale]);
  const dayNames = React.useMemo(() => getDayNames(locale), [locale]);
  const [selectedDates, setSelectedDates] = React.useState<Date[]>(
    value ? value : [],
  );

  React.useEffect(() => {
    if (typeof onChange === 'function') onChange(selectedDates);
  }, [selectedDates]);

  return (
    <Box {...props}>
      <RangeDatepicker
        selectedDates={selectedDates}
        onDateChange={setSelectedDates}
        minDate={min}
        maxDate={max}
        disabled={isDisabled}
        configs={{
          dateFormat: 'dd.MM.yyyy',
          dayNames,
          monthNames,
          firstDayOfWeek: locale.options?.weekStartsOn ?? 1,
        }}
        propsConfigs={{
          dayOfMonthBtnProps: {
            defaultBtnProps: { _hover: { bg: 'nexusTeal.100' } },
            selectedBtnProps: { background: 'nexusTeal.200', color: 'white' },
            isInRangeBtnProps: { background: 'nexusTeal.200', color: 'white' },
          },
        }}
      />
    </Box>
  );
};
